import { CalendarClock, Megaphone, Shuffle, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import usePublicEconomics from '@/hooks/usePublicEconomics';

/**
 * Visual representation of independent 3×1 mini-matrices opening,
 * pulling randomly from the global pool, then dissolving.
 * Pure decorative / illustrative — no backend.
 */
const MiniMatrix = ({ filled = [false, false, false], state = 'open' }) => {
  return (
    <div
      className="sc-glass rounded-xl p-3 relative"
      style={{
        borderColor: state === 'complete' ? 'rgba(255, 182, 18, 0.55)' : 'rgba(255, 182, 18, 0.18)',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">3×1 cell</span>
        <span
          className="text-[9px] uppercase tracking-[0.2em] font-bold"
          style={{
            color: state === 'complete' ? '#FFC20E' : '#FFB612',
          }}
        >
          {state}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {filled.map((isFilled, i) => (
          <div key={i} className={`sc-mini-cell ${isFilled ? 'sc-filled' : ''}`} style={{ aspectRatio: '1.2 / 1', fontSize: 12 }}>
            {isFilled ? '✓' : i + 1}
          </div>
        ))}
      </div>
    </div>
  );
};

const RotatorPoolVisual = () => {
  const { positionCost, heartbeat } = usePublicEconomics();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1300);
    return () => clearInterval(id);
  }, []);

  // Generate 12 mini-matrices that visually "fill" over time, dissolve, restart.
  const matrices = Array.from({ length: 12 }, (_, idx) => {
    const offset = (tick + idx) % 6;
    const filledCount = Math.min(3, offset);
    return {
      filled: [filledCount >= 1, filledCount >= 2, filledCount >= 3],
      state: filledCount === 3 ? 'complete' : filledCount === 0 ? 'open' : 'filling',
    };
  });

  return (
    <section id="how" className="relative max-w-7xl mx-auto px-5 md:px-8 pt-6 md:pt-10 pb-16 md:pb-24" data-testid="rotator-pool-visual">
      <div className="sc-glass-strong rounded-3xl p-7 md:p-12 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
          {/* Left — copy */}
          <div className="flex-1 max-w-xl">
            <div className="sc-pill">
              <span className="sc-dot" />
              Live Architecture Diagram
            </div>
            <h3 className="font-display mt-5 text-[30px] md:text-[40px] leading-[1.08] font-bold tracking-tight">
              Rapid <span className="sc-gold-text">3×1 Cycles</span> Driven by Real-Time Telemetry
            </h3>
            <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed text-[var(--sc-text-dim)]">
              Every position requires only 3 micro-fillers ({positionCost} each) to complete. As positions drip from Sub Packs and Speedy Packs, the engine
              continuously pairs roots with fillers across the alternating rotation.
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Pill
                icon={Shuffle}
                title="Alternating Heartbeat Engine"
                sub={`One cycle executes a Random pool draw; the next honors First-In, First-Out (FIFO) order — live every ${heartbeat}.`}
              />
              <Pill icon={CalendarClock} title="365-Day Sub Packs" sub="8 automated tiers steadily releasing positions for a full 52 weeks." />
              <Pill icon={Zap} title="Speedy Packs" sub="High-speed 5-minute position releases providing active withdrawal gating." />
              <Pill icon={Megaphone} title="Ad Credit Utility" sub="Every cent spent awards banner advertising impressions (1 cent = 1 ad credit)." />
            </div>
          </div>

          {/* Right — matrix grid visual */}
          <div className="flex-1 w-full">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {matrices.map((m, i) => (
                <MiniMatrix key={i} filled={m.filled} state={m.state} />
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              <span>Random &amp; FIFO heartbeats</span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612] animate-pulse" />
                Pool active
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pill = ({ icon: Icon, title, sub }) => (
  <div className="sc-glass rounded-xl p-4 flex items-start gap-3">
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center border"
      style={{
        background: 'rgba(255, 182, 18, 0.06)',
        borderColor: 'rgba(255, 182, 18, 0.28)',
      }}
    >
      <Icon className="w-4 h-4" style={{ color: '#FFC20E' }} />
    </div>
    <div>
      <div className="text-[13px] font-semibold leading-tight">{title}</div>
      <div className="text-[11px] text-[var(--sc-text-mute)] leading-snug mt-0.5">{sub}</div>
    </div>
  </div>
);

export default RotatorPoolVisual;
